import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { buildInventory, buildMaterialsPolicyMap, SYSTEM_DEFAULT_CRITICAL_PCT, SYSTEM_DEFAULT_REORDER_PCT } from '../lib/inventorySSOT'
import { generateTransferNo, loadProjectInventoryContext, submitTransfer, approveTransfer, rejectTransfer, receiveTransfer } from '../lib/transferService'
import { getRequiredApprover, APPROVAL_LIMITS } from '../lib/approvalLimits'
import { useProjectAuth } from './useProjectAuth'
import { useAuth } from '../context/AuthContext'
import * as XLSX from 'xlsx'

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtM = (n) => {
  const v = Number(n || 0)
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M'
  if (Math.abs(v) >= 1_000) return (v / 1_000).toFixed(1) + 'K'
  return v.toFixed(2)
}

const StatusBadge = ({ item }) => {
  const map = {
    negative: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', label: 'Negative' },
    out: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', label: 'Out of Stock' },
    critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', label: 'Critical' },
    low: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', label: 'Low Stock' },
    in_stock: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', label: 'In Stock' },
  }
  const c = map[item.stockStatus] || map.in_stock
  return (
    <span className={`text-xs ${c.bg} ${c.text} px-2 py-0.5 rounded-full border ${c.border} inline-flex items-center gap-1`}>
      {c.label}
      {item.stockPolicySource === 'customized' && <span title="Customized Stock Policy" className="text-[10px]">•</span>}
    </span>
  )
}

export default function InventoryManagement({ initProject }) {
  const [activeTab, setActiveTab] = useState('overview') // overview | inventory | movements | transfers
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(initProject || null)
  const [loading, setLoading] = useState(false)

  // Real authorization source -- can() mirrors has_permission() exactly,
  // same hook pattern as IPC/PO/PR/EquipmentPR/Subcontract. Called ONCE
  // at the top level (never inside rows/loops/conditionals), always for
  // selectedProject.id -- the Transfers table only ever shows transfers
  // where selectedProject is either the from_project_id or to_project_id
  // (confirmed by the .or() filter in loadData() below), so this single
  // can() correctly covers both the "source" and "destination" viewing
  // context per row via the from/to comparison done in TransfersTab.
  const { can } = useProjectAuth(selectedProject?.id)
  // Real authenticated user's display name for audit fields
  // (cc_approved_by/received_by), replacing the removed tempRole-based
  // ROLE_LABELS[tempRole] placeholder.
  const { profile } = useAuth()
  const currentUserName = profile?.full_name || profile?.email || 'Unknown User'

  const [receipts, setReceipts] = useState([])
  const [issues, setIssues] = useState([])
  const [materialsMaster, setMaterialsMaster] = useState([])
  const [transferOutLines, setTransferOutLines] = useState([])
  const [transferInLines, setTransferInLines] = useState([])
  const [allTransfers, setAllTransfers] = useState([])

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [drillDown, setDrillDown] = useState(null)
  const [movementSearch, setMovementSearch] = useState('')

  const [showTransferModal, setShowTransferModal] = useState(false)

  // ── Data loading (F-01 pattern: paginate to avoid any row cap) ──────────
  async function fetchAllRows(queryFn) {
    const PAGE = 1000
    let from = 0, all = []
    while (true) {
      const { data } = await queryFn(from, from + PAGE - 1)
      if (!data?.length) break
      all = all.concat(data)
      if (data.length < PAGE) break
      from += PAGE
    }
    return all
  }

  const loadData = useCallback(async (projectId) => {
    if (!projectId) return
    setLoading(true)
    const [receiptsAll, issuesAll, materialsRes, transferOutRes, transferInRes, allTransfersRes] = await Promise.all([
      fetchAllRows((from, to) =>
        supabase.from('store_receipts').select('ref_code,inventory_ref,material,unit,qty_received,total_exc_vat,received_date')
          .eq('project_id', projectId).range(from, to)),
      fetchAllRows((from, to) =>
        supabase.from('store_issues').select('ref_code,inventory_ref,material,unit,qty_issued,issued_date')
          .eq('project_id', projectId).range(from, to)),
      supabase.from('materials').select('material_code,description,unit,category,threshold_type,critical_level,reorder_level').eq('project_id', projectId),
      supabase.from('inventory_transfer_lines')
        .select('ref_code,material,unit,qty,source_wac,transfer_id,inventory_transfers!inner(from_project_id,status,transfer_no,transfer_date)')
        .eq('inventory_transfers.from_project_id', projectId).eq('inventory_transfers.status', 'approved'),
      supabase.from('inventory_transfer_lines')
        .select('ref_code,material,unit,qty,source_wac,transfer_id,inventory_transfers!inner(to_project_id,status,transfer_no,transfer_date)')
        .eq('inventory_transfers.to_project_id', projectId).eq('inventory_transfers.status', 'approved'),
      supabase.from('inventory_transfers').select('*, inventory_transfer_lines(*)')
        .or(`from_project_id.eq.${projectId},to_project_id.eq.${projectId}`)
        .order('created_at', { ascending: false }),
    ])
    setReceipts(receiptsAll || [])
    setIssues(issuesAll || [])
    setMaterialsMaster(materialsRes.data || [])
    setTransferOutLines(transferOutRes.data || [])
    setTransferInLines(transferInRes.data || [])
    setAllTransfers(allTransfersRes.data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    supabase.from('projects').select('id,name,currency,organizations(name)').order('name').then(({ data }) => setProjects(data || []))
  }, [])

  useEffect(() => {
    if (selectedProject) loadData(selectedProject.id)
  }, [selectedProject, loadData])

  // ── Single Inventory SSOT (inventorySSOT.js) ─────────────────────────────
  const materialsPolicyMap = buildMaterialsPolicyMap(materialsMaster)
  const inventory = buildInventory(receipts, issues, materialsPolicyMap, transferOutLines, transferInLines)

  const materialsByRef = {}
  materialsMaster.forEach(m => { materialsByRef[(m.material_code || '').trim().toUpperCase()] = m })

  const filteredInventory = inventory.filter(i => {
    if (search && !i.material?.toLowerCase().includes(search.toLowerCase()) && !i.ref_code?.toLowerCase().includes(search.toLowerCase())) return false
    if (statusFilter !== 'all' && i.stockStatus !== statusFilter) return false
    if (categoryFilter !== 'all') {
      const cat = materialsByRef[(i.ref_code || '').trim().toUpperCase()]?.category
      if (cat !== categoryFilter) return false
    }
    return true
  })

  const categories = [...new Set(materialsMaster.map(m => m.category).filter(Boolean))]

  // ── KPIs (all derived from the same inventory array — no separate calc) ──
  const kpis = {
    totalItems: inventory.length,
    totalStockQty: inventory.reduce((s, i) => s + i.balance, 0),
    totalStockValue: inventory.reduce((s, i) => s + i.stockValue, 0),
    lowStockItems: inventory.filter(i => i.stockStatus === 'low').length,
    criticalItems: inventory.filter(i => i.stockStatus === 'critical' || i.stockStatus === 'negative').length,
    recentReceipts: receipts.length,
    recentIssues: issues.length,
    transfersPending: allTransfers.filter(t => ['draft', 'cost_control'].includes(t.status)).length,
    transferInTotal: transferInLines.reduce((s, l) => s + (parseFloat(l.qty) || 0), 0),
    transferOutTotal: transferOutLines.reduce((s, l) => s + (parseFloat(l.qty) || 0), 0),
  }


  return (
    <div className="min-h-screen bg-[#F9FAFB] p-6">
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '18px 24px 14px', marginBottom: 16, marginLeft: -24, marginRight: -24, marginTop: -24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0F1017', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 3 }}>Inventory Management</h1>
          <p style={{ fontSize: 11.5, color: '#6B7280', marginTop: 3, lineHeight: 1.4 }}>Professional Warehouse Workspace — Overview · Inventory · Movements · Transfers</p>
        </div>
      </div>

      {/* Project Selector */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 mb-4">
        <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Select Project</label>
        <div className="flex gap-2 flex-wrap">
          {projects.map(p => (
            <button key={p.id} onClick={() => setSelectedProject(p)}
              className={`px-4 py-2 rounded-lg border text-sm text-left ${selectedProject?.id === p.id ? 'bg-[#EDE7DA] border-[#1B3A6B]' : 'border-[#E5E7EB] hover:bg-[#FAFAFA]'}`}>
              <div className="font-semibold text-[#1C1A18]">{p.name}</div>
              <div className="text-xs text-[#9CA3AF]">{p.organizations?.name}</div>
            </button>
          ))}
        </div>
      </div>

      {selectedProject && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 bg-[#F5F1EA] rounded-xl p-1 mb-4 w-fit">
            {[
              { id: 'overview', label: '📊 Overview' },
              { id: 'inventory', label: '📦 Inventory' },
              { id: 'movements', label: '📋 Movements' },
              { id: 'transfers', label: '🔄 Transfers' },
            ].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === t.id ? 'bg-[#1B3A6B] text-white' : 'text-[#6B7280] hover:bg-white'}`}>
                {t.label}
              </button>
            ))}
          </div>

          {loading && <div className="text-center py-8 text-[#9CA3AF]">Loading...</div>}

          {!loading && activeTab === 'overview' && (
            <OverviewTab kpis={kpis} inventory={inventory} />
          )}

          {!loading && activeTab === 'inventory' && (
            <InventoryTab
              filteredInventory={filteredInventory} materialsByRef={materialsByRef}
              search={search} setSearch={setSearch}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
              categories={categories} setDrillDown={setDrillDown}
              selectedProject={selectedProject}
            />
          )}

          {!loading && activeTab === 'movements' && (
            <MovementsTab receipts={receipts} issues={issues} transferOutLines={transferOutLines} transferInLines={transferInLines} movementSearch={movementSearch} setMovementSearch={setMovementSearch} />
          )}

          {!loading && activeTab === 'transfers' && (
            <TransfersTab
              allTransfers={allTransfers} projects={projects} selectedProject={selectedProject}
              onCreateNew={() => setShowTransferModal(true)}
              onReload={() => loadData(selectedProject.id)}
              can={can} currentUserName={currentUserName}
            />
          )}

          {drillDown && (
            <MaterialDrillDown item={drillDown} receipts={receipts} issues={issues}
              transferOutLines={transferOutLines} transferInLines={transferInLines}
              onClose={() => setDrillDown(null)} />
          )}

          {showTransferModal && (
            <TransferModal projects={projects} selectedProject={selectedProject}
              onClose={() => setShowTransferModal(false)}
              onCreated={() => { setShowTransferModal(false); loadData(selectedProject.id) }} />
          )}
        </>
      )}
    </div>
  )
}

// ============================================================================
// Overview Tab — KPI Dashboard
// ============================================================================
function OverviewTab({ kpis, inventory }) {
  const cards = [
    { label: 'Total Items', value: kpis.totalItems, color: '#1B3A6B' },
    { label: 'Total Stock Qty', value: fmt(kpis.totalStockQty), color: '#1A7A4A' },
    { label: 'Total Stock Value', value: fmtM(kpis.totalStockValue), color: '#B7770D' },
    { label: 'Low Stock Items', value: kpis.lowStockItems, color: '#B7770D' },
    { label: 'Critical Items', value: kpis.criticalItems, color: '#C0392B' },
    { label: 'Transfers Pending', value: kpis.transfersPending, color: '#6B7280' },
    { label: 'Transfer In (Total Qty)', value: fmt(kpis.transferInTotal), color: '#1A7A4A' },
    { label: 'Transfer Out (Total Qty)', value: fmt(kpis.transferOutTotal), color: '#C0392B' },
  ]
  return (
    <div className="grid grid-cols-4 gap-4">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
          <div className="text-2xl font-bold" style={{ color: c.color }}>{c.value}</div>
          <div className="text-xs text-[#6B7280] mt-1">{c.label}</div>
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Inventory Master Tab
// ============================================================================
function InventoryTab({ filteredInventory, materialsByRef, search, setSearch, statusFilter, setStatusFilter, categoryFilter, setCategoryFilter, categories, setDrillDown, selectedProject }) {
  const exportExcel = () => {
    const rows = filteredInventory.map(i => ({
      'Ref Code': i.ref_code, 'Material': i.material,
      'Category': materialsByRef[(i.ref_code || '').trim().toUpperCase()]?.category || '',
      'Unit': i.unit,
      'Received': i.pureReceived, 'Issued': i.issued,
      'Transfer In': i.transferIn, 'Transfer Out': i.transferOut,
      'Closing Balance': i.balance, 'WAC': i.weightedAvgRate, 'Stock Value': i.stockValue,
      'Status': i.stockStatusLabel,
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Summary')
    XLSX.writeFile(wb, `inventory_summary_${selectedProject.name}.xlsx`)
  }

  const exportPDF = () => {
    const today = new Date().toLocaleDateString('en-GB')
    const totalStockValue = filteredInventory.reduce((s, i) => s + i.stockValue, 0)
    const rows = filteredInventory.map((i, idx) => `
      <tr style="background:${idx % 2 === 0 ? '#f8fafc' : '#fff'}">
        <td style="padding:5px 8px;font-family:monospace;color:#1B3A6B;font-size:11px">${i.ref_code || '—'}</td>
        <td style="padding:5px 8px">${i.material || '—'}</td>
        <td style="padding:5px 8px;text-align:center">${i.unit || '—'}</td>
        <td style="padding:5px 8px;text-align:right">${fmt(i.pureReceived)}</td>
        <td style="padding:5px 8px;text-align:right">${fmt(i.issued)}</td>
        <td style="padding:5px 8px;text-align:right;color:#1A7A4A">${fmt(i.transferIn)}</td>
        <td style="padding:5px 8px;text-align:right;color:#C0392B">${fmt(i.transferOut)}</td>
        <td style="padding:5px 8px;text-align:right;font-weight:700">${fmt(i.balance)}</td>
        <td style="padding:5px 8px;text-align:right">${fmt(i.weightedAvgRate)}</td>
        <td style="padding:5px 8px;text-align:right;font-weight:700">${fmt(i.stockValue)}</td>
        <td style="padding:5px 8px;text-align:center;font-weight:700">${i.stockStatusLabel}</td>
      </tr>`).join('')
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Inventory Summary — ${selectedProject.name}</title>
    <style>
      @page { size: A4 landscape; margin: 12mm; }
      *{box-sizing:border-box;margin:0;padding:0} body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b}
      .header{background:#0F2744;color:white;padding:12px 18px;border-radius:8px;margin-bottom:14px;display:flex;justify-content:space-between}
      table{width:100%;border-collapse:collapse;font-size:10px}
      thead th{background:#0F2744;color:white;padding:6px 7px;text-align:left}
      tfoot td{background:#f1f5f9;font-weight:700;padding:6px 7px;border-top:2px solid #cbd5e1}
    </style></head><body>
    <div class="header"><div><h1 style="font-size:17px">Inventory Summary Report</h1><p style="font-size:10px">${selectedProject.name}</p></div>
    <div style="text-align:right"><p style="font-weight:700">${today}</p><p style="font-size:10px">CostSpace Pro</p></div></div>
    <table><thead><tr><th>Ref Code</th><th>Material</th><th>Unit</th><th>Received</th><th>Issued</th><th>Transfer In</th><th>Transfer Out</th><th>Balance</th><th>WAC</th><th>Stock Value</th><th>Status</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="7">TOTAL</td><td></td><td></td><td>${fmt(totalStockValue)}</td><td></td></tr></tfoot></table>
    </body></html>`
    const w = window.open('', '_blank')
    w.document.write(html); w.document.close(); w.print()
  }

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
      <div className="p-4 border-b border-[#F3F4F6] flex gap-2 items-center flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search material or ref code..."
          className="flex-1 min-w-[200px] border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
          <option value="all">All Status</option>
          <option value="critical">Critical</option>
          <option value="low">Low Stock</option>
          <option value="in_stock">In Stock</option>
          <option value="out">Out of Stock</option>
          <option value="negative">Negative</option>
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={exportExcel} className="px-3 py-2 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]">Excel</button>
        <button onClick={exportPDF} className="px-3 py-2 text-xs bg-[#1A7A4A] text-white rounded-lg hover:bg-[#15693F]">PDF</button>
        <span className="text-xs text-[#9CA3AF]">{filteredInventory.length} materials</span>
      </div>
      <div className="overflow-x-auto max-h-[600px]">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] sticky top-0">
            <tr>{['Ref Code', 'Material', 'Unit', 'Received', 'Issued', 'Transfer In', 'Transfer Out', 'Closing', 'WAC', 'Stock Value', 'Status'].map(h => (
              <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">{h}</th>
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {filteredInventory.map((item, i) => (
              <tr key={i} className="hover:bg-[#FAFAFA] cursor-pointer" onClick={() => setDrillDown(item)}>
                <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1B3A6B]">{item.ref_code || '—'}</td>
                <td className="px-4 py-3">{item.material}</td>
                <td className="px-4 py-3 text-xs text-[#6B7280]">{item.unit || '—'}</td>
                <td className="px-4 py-3 text-right text-[#1A7A4A]">{fmt(item.pureReceived)}</td>
                <td className="px-4 py-3 text-right text-[#1A7A4A]">{fmt(item.issued)}</td>
                <td className="px-4 py-3 text-right text-[#1A7A4A]">{item.transferIn > 0 ? fmt(item.transferIn) : '—'}</td>
                <td className="px-4 py-3 text-right text-[#C0392B]">{item.transferOut > 0 ? fmt(item.transferOut) : '—'}</td>
                <td className="px-4 py-3 text-right font-bold">{fmt(item.balance)}</td>
                <td className="px-4 py-3 text-right text-xs text-[#6B7280]">{fmt(item.weightedAvgRate)}</td>
                <td className="px-4 py-3 text-right font-semibold text-amber-700">{fmt(item.stockValue)}</td>
                <td className="px-4 py-3"><StatusBadge item={item} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================================
// Movements Tab — Unified ledger (Receipt/Issue/Transfer In/Transfer Out)
// ============================================================================
function MovementsTab({ receipts, issues, transferOutLines, transferInLines, movementSearch, setMovementSearch }) {
  const movements = [
    ...receipts.map(r => ({ date: r.received_date, type: 'Receipt', ref_code: r.ref_code, material: r.material, qty: r.qty_received, unit: r.unit, value: r.total_exc_vat })),
    ...issues.map(i => ({ date: i.issued_date, type: 'Issue', ref_code: i.ref_code, material: i.material, qty: -i.qty_issued, unit: i.unit, value: null })),
    ...transferOutLines.map(l => ({ date: l.inventory_transfers?.transfer_date, type: 'Transfer Out', ref_code: l.ref_code, material: l.material, qty: -l.qty, unit: l.unit, value: l.qty * (l.source_wac || 0), ref: l.inventory_transfers?.transfer_no })),
    ...transferInLines.map(l => ({ date: l.inventory_transfers?.transfer_date, type: 'Transfer In', ref_code: l.ref_code, material: l.material, qty: l.qty, unit: l.unit, value: l.qty * (l.source_wac || 0), ref: l.inventory_transfers?.transfer_no })),
  ]
    .filter(m => !movementSearch || m.material?.toLowerCase().includes(movementSearch.toLowerCase()) || m.ref_code?.toLowerCase().includes(movementSearch.toLowerCase()))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))

  const typeColor = { Receipt: 'text-[#1A7A4A]', Issue: 'text-[#C0392B]', 'Transfer In': 'text-[#1B3A6B]', 'Transfer Out': 'text-[#B7770D]' }

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
      <div className="p-4 border-b border-[#F3F4F6] flex items-center gap-2">
        <input value={movementSearch} onChange={e => setMovementSearch(e.target.value)} placeholder="Search material or ref code..."
          className="flex-1 border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
        <span className="text-xs text-[#9CA3AF] whitespace-nowrap">{movements.length} movements</span>
      </div>
      <div className="overflow-x-auto max-h-[600px]">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] sticky top-0">
            <tr>{['Date', 'Type', 'Ref Code', 'Material', 'Qty', 'Unit', 'Value', 'Reference'].map(h => (
              <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">{h}</th>
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {movements.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-[#9CA3AF] text-sm">No movements found</td></tr>
            ) : movements.map((m, i) => (
              <tr key={i} className="hover:bg-[#FAFAFA]">
                <td className="px-4 py-3 text-xs text-[#6B7280]">{m.date || '—'}</td>
                <td className={`px-4 py-3 text-xs font-semibold ${typeColor[m.type]}`}>{m.type}</td>
                <td className="px-4 py-3 font-mono text-xs">{m.ref_code || '—'}</td>
                <td className="px-4 py-3">{m.material}</td>
                <td className={`px-4 py-3 text-right font-medium ${typeColor[m.type]}`}>{m.qty > 0 ? '+' : ''}{fmt(m.qty)}</td>
                <td className="px-4 py-3 text-xs">{m.unit || '—'}</td>
                <td className="px-4 py-3 text-right text-xs">{m.value != null ? fmt(m.value) : '—'}</td>
                <td className="px-4 py-3 text-xs text-[#9CA3AF]">{m.ref || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================================
// Material Drill-Down (full movement history for one material)
// ============================================================================
function MaterialDrillDown({ item, receipts, issues, transferOutLines, transferInLines, onClose }) {
  const key = (item.ref_code || item.material || '').trim().toUpperCase()
  const rows = [
    { type: 'OPENING BALANCE', date: '', qty: '', balance: 0 },
  ]
  const movs = [
    ...receipts.filter(r => (r.ref_code || r.material || '').trim().toUpperCase() === key).map(r => ({ type: 'Receipt', date: r.received_date, qty: r.qty_received })),
    ...issues.filter(i => (i.ref_code || i.material || '').trim().toUpperCase() === key).map(i => ({ type: 'Issue', date: i.issued_date, qty: -i.qty_issued })),
    ...transferOutLines.filter(l => (l.ref_code || '').trim().toUpperCase() === key).map(l => ({ type: 'Transfer Out', date: l.inventory_transfers?.transfer_date, qty: -l.qty, ref: l.inventory_transfers?.transfer_no })),
    ...transferInLines.filter(l => (l.ref_code || '').trim().toUpperCase() === key).map(l => ({ type: 'Transfer In', date: l.inventory_transfers?.transfer_date, qty: l.qty, ref: l.inventory_transfers?.transfer_no })),
  ].sort((a, b) => (a.date || '').localeCompare(b.date || ''))

  let running = 0
  movs.forEach(m => { running += m.qty; rows.push({ ...m, balance: running }) })
  rows.push({ type: 'CLOSING BALANCE', date: '', qty: '', balance: item.balance })

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold">{item.material}</h3>
            <p className="text-xs font-mono text-[#6B7280]">{item.ref_code}</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#1C1A18]">✕</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-xs text-[#6B7280] uppercase border-b"><th className="text-left py-2">Date</th><th className="text-left py-2">Type</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Balance</th><th className="text-left py-2">Ref</th></tr></thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {rows.map((r, i) => (
              <tr key={i} className={r.type.includes('BALANCE') ? 'font-bold bg-[#F9FAFB]' : ''}>
                <td className="py-2 text-xs">{r.date || ''}</td>
                <td className="py-2 text-xs">{r.type}</td>
                <td className="py-2 text-right">{r.qty !== '' ? fmt(r.qty) : ''}</td>
                <td className="py-2 text-right font-medium">{fmt(r.balance)}</td>
                <td className="py-2 text-xs text-[#9CA3AF]">{r.ref || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================================
// Transfers Tab — Register + Actions
// ============================================================================
function TransfersTab({ allTransfers, projects, selectedProject, onCreateNew, onReload, can, currentUserName }) {
  const [processingId, setProcessingId] = useState(null)
  const projectName = (id) => projects.find(p => p.id === id)?.name || id

  const statusColor = { draft: 'bg-gray-100 text-gray-700', cost_control: 'bg-amber-100 text-amber-700', approved: 'bg-blue-100 text-blue-700', received: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' }
  const statusLabel = { draft: 'Draft', cost_control: 'Pending (Cost Control)', approved: 'Approved — Awaiting Receipt', received: 'Received', rejected: 'Rejected' }

  const handleSubmit = async (t) => {
    setProcessingId(t.id)
    await submitTransfer(t.id)
    setProcessingId(null); onReload()
  }
  const handleApprove = async (t) => {
    setProcessingId(t.id)
    const { error } = await approveTransfer({ ...t, lines: t.inventory_transfer_lines }, currentUserName)
    if (error) alert(error.message)
    setProcessingId(null); onReload()
  }
  const handleReject = async (t) => {
    const reason = window.prompt('Rejection reason:')
    if (reason == null) return
    setProcessingId(t.id)
    await rejectTransfer(t.id, reason)
    setProcessingId(null); onReload()
  }
  const handleReceive = async (t) => {
    if (!window.confirm(`Confirm physical receipt of transfer ${t.transfer_no} into ${projectName(t.to_project_id)}?`)) return
    setProcessingId(t.id)
    const { error } = await receiveTransfer(t.id, currentUserName)
    if (error) alert(error.message)
    setProcessingId(null); onReload()
  }

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
      <div className="p-4 border-b border-[#F3F4F6] flex justify-between items-center">
        <h3 className="text-sm font-semibold">Transfer Register</h3>
        {can('Inventory.Create') && (
          <button onClick={onCreateNew} className="px-4 py-2 text-sm bg-[#1B3A6B] text-white rounded-lg hover:bg-[#1B3A6B]/90">+ New Transfer</button>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <tr>{['Transfer No.', 'Date', 'From', 'To', 'Status', 'Materials', 'Value', 'Actions'].map(h => (
            <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">{h}</th>
          ))}</tr>
        </thead>
        <tbody className="divide-y divide-[#F3F4F6]">
          {allTransfers.map(t => {
            const totalValue = (t.inventory_transfer_lines || []).reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.source_wac) || 0), 0)
            // Authorization context is determined purely by comparing
            // selectedProject.id (the single project this can() was
            // computed for) against this row's own from/to -- no
            // additional useProjectAuth() calls, no hooks inside the
            // loop. The Transfers list only ever contains rows where
            // selectedProject is either the source or destination
            // (confirmed via the .or() filter in loadData()), so this
            // single can() correctly covers both viewing contexts.
            const isViewingSource = selectedProject.id === t.from_project_id
            const isViewingDestination = selectedProject.id === t.to_project_id
            return (
              <tr key={t.id} className="hover:bg-[#FAFAFA]">
                <td className="px-4 py-3 font-mono text-xs text-[#1B3A6B]">{t.transfer_no}</td>
                <td className="px-4 py-3 text-xs">{t.transfer_date}</td>
                <td className="px-4 py-3 text-xs">{projectName(t.from_project_id)}</td>
                <td className="px-4 py-3 text-xs">{projectName(t.to_project_id)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[t.status]}`}>{statusLabel[t.status] || t.status}</span>
                  {t.status === 'rejected' && t.rejection_reason && (
                    <p className="text-[10px] text-red-600 mt-1 italic">{t.rejection_reason}</p>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">{(t.inventory_transfer_lines || []).length}</td>
                <td className="px-4 py-3 text-right text-xs font-medium">{fmt(totalValue)}</td>
                <td className="px-4 py-3 text-xs">
                  {t.status === 'draft' && isViewingSource && can('Inventory.Submit') && <button disabled={processingId === t.id} onClick={() => handleSubmit(t)} className="text-[#1B3A6B] underline">Submit</button>}
                  {t.status === 'cost_control' && isViewingSource && can('Inventory.ApproveCostControl') && (
                    <span className="flex gap-2">
                      <button disabled={processingId === t.id} onClick={() => handleApprove(t)} className="text-[#1A7A4A] underline">Approve</button>
                      <button disabled={processingId === t.id} onClick={() => handleReject(t)} className="text-[#C0392B] underline">Reject</button>
                    </span>
                  )}
                  {t.status === 'approved' && isViewingDestination && can('Inventory.Receive') && (
                    <button disabled={processingId === t.id} onClick={() => handleReceive(t)} className="text-[#1A7A4A] underline font-semibold">Receive</button>
                  )}
                  {t.status === 'approved' && !isViewingDestination && (
                    <span className="text-[#9CA3AF] italic">Awaiting receipt at {projectName(t.to_project_id)}</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ============================================================================
// New Transfer Modal — Header + Lines
// ============================================================================
function TransferModal({ projects, selectedProject, onClose, onCreated }) {
  const [toProjectId, setToProjectId] = useState('')
  const [reason, setReason] = useState('')
  const [lines, setLines] = useState([{ ref_code: '', material: '', unit: '', qty: '', currentWac: 0 }])
  const [sourceInventory, setSourceInventory] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadProjectInventoryContext(selectedProject.id).then(({ inventory }) => setSourceInventory(inventory))
  }, [selectedProject.id])

  const addLine = () => setLines([...lines, { ref_code: '', material: '', unit: '', qty: '', currentWac: 0 }])
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx))
  const updateLine = (idx, field, value) => {
    const next = [...lines]
    next[idx][field] = value
    if (field === 'ref_code') {
      const match = sourceInventory.find(i => (i.ref_code || '').trim().toUpperCase() === value.trim().toUpperCase())
      if (match) {
        next[idx].material = match.material
        next[idx].unit = match.unit
        next[idx].currentWac = match.weightedAvgRate
        next[idx].availableBalance = match.balance
      }
    }
    setLines(next)
  }

  const totalValue = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.currentWac) || 0), 0)

  const handleSave = async () => {
    setError('')
    if (!toProjectId) return setError('Select destination project')
    if (toProjectId === selectedProject.id) return setError('Destination must differ from source')
    const validLines = lines.filter(l => l.ref_code && parseFloat(l.qty) > 0)
    if (!validLines.length) return setError('Add at least one valid line')

    setSaving(true)
    const transferNo = await generateTransferNo()
    const { data: transfer, error: insErr } = await supabase.from('inventory_transfers').insert({
      transfer_no: transferNo, from_project_id: selectedProject.id, to_project_id: toProjectId,
      transfer_date: new Date().toISOString().slice(0, 10), reason, status: 'draft', approval_stage: 'draft',
    }).select().single()
    if (insErr) { setError(insErr.message); setSaving(false); return }

    const lineRows = validLines.map(l => ({
      transfer_id: transfer.id, ref_code: l.ref_code.trim().toUpperCase(), material: l.material, unit: l.unit,
      qty: parseFloat(l.qty), source_wac: l.currentWac, // informational at Draft time; re-captured fresh at approval
    }))
    const { error: lineErr } = await supabase.from('inventory_transfer_lines').insert(lineRows)
    if (lineErr) { setError(lineErr.message); setSaving(false); return }

    setSaving(false)
    onCreated()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">New Inventory Transfer</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">From Project</label>
            <div className="px-3 py-2 bg-[#F9FAFB] rounded-lg text-sm">{selectedProject.name}</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">To Project *</label>
            <select value={toProjectId} onChange={e => setToProjectId(e.target.value)} className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
              <option value="">Select...</option>
              {projects.filter(p => p.id !== selectedProject.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Reason</label>
          <input value={reason} onChange={e => setReason(e.target.value)} className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
        </div>

        <label className="block text-xs font-semibold text-[#6B7280] uppercase mb-2">Lines</label>
        {lines.map((l, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center">
            <input value={l.ref_code} onChange={e => updateLine(idx, 'ref_code', e.target.value)} placeholder="Ref Code" className="col-span-3 border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-xs" />
            <div className="col-span-3 text-xs text-[#6B7280] truncate">{l.material}</div>
            <input type="number" value={l.qty} onChange={e => updateLine(idx, 'qty', e.target.value)} placeholder="Qty" className="col-span-2 border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-xs" />
            <div className="col-span-2 text-xs text-[#9CA3AF]">WAC: {fmt(l.currentWac)}</div>
            <div className="col-span-1 text-xs">{l.availableBalance != null ? `Avail: ${fmt(l.availableBalance)}` : ''}</div>
            <button onClick={() => removeLine(idx)} className="col-span-1 text-[#C0392B] text-xs">✕</button>
          </div>
        ))}
        <button onClick={addLine} className="text-xs text-[#1B3A6B] underline mb-4">+ Add Line</button>

        <div className="bg-[#F9FAFB] rounded-lg p-3 mb-4 text-sm flex justify-between">
          <span className="text-[#6B7280]">Estimated Total Value (informational — WAC re-captured at approval)</span>
          <span className="font-semibold">{fmt(totalValue)}</span>
        </div>

        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-[#1B3A6B] text-white rounded-lg disabled:opacity-50">
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
        </div>
      </div>
    </div>
  )
}
